import "./style.scss";

import Import from "assets/img/svg/import";
import ModalBox from "components/Modal";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import { formatLocaleDateString } from "helpers/utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { downloadSageAccountingFile, fetchSageAccountingUploadHistory } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import LastElementTable from "../../LastElementTable";
import type { LastElementTableColumns } from "../../LastElementTable/type";
import StyledButton from "../../StyledButton";

interface SageHistoryModalProps {
    open: boolean;
    onClose: () => void;
}

const SageHistoryModal = ({ open, onClose }: SageHistoryModalProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { sageUploadHistory, sageUploadHistoryStatus } = useAppSelector((state) => state.payment);

    useEffect(() => {
        if (open) {
            dispatch(fetchSageAccountingUploadHistory());
        }
    }, [open, dispatch]);

    const handleDownload = (fileId: number, fileName: string) => {
        dispatch(downloadSageAccountingFile({ id: fileId, fileName }));
    };

    const sageStatusRules: Record<string, IStatusRule> = {
        pending: {
            color: "yellow",
            text: t("PaymentModal.SageHistory.StatusPending"),
        },
        processing: {
            color: "blue",
            text: t("PaymentModal.SageHistory.StatusProcessing"),
        },
        uploaded: {
            color: "green",
            text: t("PaymentModal.SageHistory.StatusUploaded"),
        },
        failed: {
            color: "red",
            text: t("PaymentModal.SageHistory.StatusFailed"),
        },
    };

    const columns: LastElementTableColumns[] = [
        {
            id: "fileName",
            label: t("PaymentModal.SageHistory.FileName"),
            align: "left",
        },
        {
            id: "createdAt",
            label: t("PaymentModal.SageHistory.UploadDate"),
            align: "left",
            sort: true,
            format: (value: string) => {
                return formatLocaleDateString({
                    date: new Date(value),
                    options: { numeric: true, showTime: true, timeZone: "UTC" },
                });
            },
        },
        {
            id: "status",
            label: t("PaymentModal.SageHistory.Status"),
            align: "left",
            format: (value: string) => {
                return <StatusChip status={value} statusRules={sageStatusRules} />;
            },
        },
        {
            id: "fileSize",
            label: t("PaymentModal.SageHistory.FileSize"),
            align: "right",
            format: (value: number | null) => {
                if (!value) return "-";
                const kb = value / 1024;
                return `${kb.toFixed(2)} KB`;
            },
        },
        {
            id: "action",
            label: t("PaymentModal.SageHistory.File"),
            align: "right",
            format: (_value: any, id?: number, row?: any) => {
                if (!id || !row) return null;
                return (
                    <StyledButton
                        title={t("PaymentModal.SageHistory.DownloadFile")}
                        buttonType="secondary"
                        size="small"
                        onClick={() => handleDownload(id, row.fileName)}
                    >
                        <Import width="14" height="14" />
                    </StyledButton>
                );
            },
        },
    ];

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            showCloseButton
            mainTitle={t("PaymentModal.SageHistory.Title")}
            subTitle={t("PaymentModal.SageHistory.Subtitle")}
        >
            <div className="sage-history-modal-content">
                <LastElementTable
                    columns={columns}
                    data={sageUploadHistory || []}
                    isLoading={sageUploadHistoryStatus === "pending"}
                    max={-1}
                    dataType="sageUploadHistory"
                    columnsNumber={columns.length}
                    stickyFooter
                    stickyHeader
                    maxHeight="60vh"
                />
            </div>
        </ModalBox>
    );
};

export default SageHistoryModal;
